import React, { useState, useEffect, useRef } from "react";
import {
    Box, Button, Typography, Switch, TextField, Dialog,
    DialogActions, DialogContent, DialogTitle, Menu, MenuItem,
    IconButton, useMediaQuery, Paper, Stack, useTheme, FormControl, InputLabel, Select
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import API from "../api/axiosInstance";
import { Automation } from "../types/interfaces";
import AutomationEditor from "./AutomationEditor";
import SafeAgGrid from "./SafeAgGrid";

const AutomationPanel: React.FC<{ tableId: string }> = ({ tableId }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [automations, setAutomations] = useState<Automation[]>([]);
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [cloneName, setCloneName] = useState("");
    const [editorOpen, setEditorOpen] = useState(false);
    const [editAutomation, setEditAutomation] = useState<Automation | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newTrigger, setNewTrigger] = useState("INSERT");

    const gridRef = useRef<AgGridReact>(null);
    const selected = gridRef.current?.api?.getSelectedRows()?.[0] || null;

    useEffect(() => {
        loadAutomations();
    }, [tableId]);

    const getHeaders = () => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        return {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId
            }
        };
    };

    const loadAutomations = async () => {
        try {
            const res = await API.get(`/tables/${tableId}/automations`, getHeaders());
            setAutomations(res.data);
        } catch (err) {
            console.error("Error loading automations", err);
        }
    };

    const addNewAutomation = async () => {
        try {
            const newAutomation = {
                name: newName,
                triggerEvent: newTrigger,
                tableId : tableId,
                enabled: false,
                steps: []
            };
            await API.post(`/tables/${tableId}/automations`, newAutomation, getHeaders());
            setAddDialogOpen(false);
            setNewName("");
            setNewTrigger("INSERT");
            loadAutomations();
        } catch (err) {
            console.error("Add error", err);
        }
    };

    const handleSave = async () => {
        setEditorOpen(false);
        setEditAutomation(null);
        await loadAutomations();
    };

    const handleEditClick = (automation: Automation) => {
        setEditAutomation({ ...automation });
        setEditorOpen(true);
    };

    const toggleEnabled = async (automation: Automation) => {
        try {
            await API.put(`/tables/automations/${automation.automationID}`, {
                ...automation,
                enabled: !automation.enabled
            }, getHeaders());
            loadAutomations();
        } catch (err) {
            console.error("Toggle error", err);
        }
    };

    const deleteAutomation = async (automationID: string) => {
        if (!window.confirm("Delete this automation?")) return;
        try {
            await API.delete(`/tables/automations/${automationID}`, getHeaders());
            loadAutomations();
        } catch (err) {
            console.error("Delete error", err);
        }
    };

    const cloneAutomation = async () => {
        if (!selected || !cloneName.trim()) return;
        try {
            const clone = {
                ...selected,
                name: cloneName,
                automationID: undefined,
                enabled: false
            };
            await API.post(`/tables/${tableId}/automations`, clone, getHeaders());
            setCloneModalOpen(false);
            setCloneName("");
            loadAutomations();
        } catch (err) {
            console.error("Clone error", err);
        }
    };

    const exportAutomations = () => {
        const blob = new Blob([JSON.stringify(automations, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `automations-${tableId}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importAutomations = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const imported = JSON.parse(reader.result as string);
                for (const item of imported) {
                    await API.post(`/tables/${tableId}/automations`, {
                        ...item,
                        automationID: undefined
                    }, getHeaders());
                }
                loadAutomations();
            } catch (err) {
                console.error("Import error", err);
            }
        };
        reader.readAsText(file);
    };

    const columnDefs: ColDef[] = [
        { field: "name", headerName: "Automation Name", flex: 1 },
        { field: "triggerEvent", headerName: "Trigger Event", flex: 1 },
        {
            field: "enabled",
            headerName: "Enabled",
            cellRenderer: (params: any) => (
                <Switch checked={params.value} onChange={() => toggleEnabled(params.data)} />
            )
        },
        {
            headerName: "Actions",
            field: "actions",
            cellRenderer: (params: any) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => handleEditClick(params.data)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => deleteAutomation(params.data.automationID)}>Delete</Button>
                </Stack>
            )
        }
    ];

    const renderToolbar = () => (
        isSmallScreen ? (
            <>
                <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                    <MenuItem onClick={() => setAddDialogOpen(true)}>Add</MenuItem>
                    <MenuItem onClick={() => setCloneModalOpen(true)} disabled={!selected}>Clone</MenuItem>
                    <MenuItem onClick={exportAutomations}>Export</MenuItem>
                    <MenuItem>
                        <label style={{ width: '100%' }}>
                            Import
                            <input type="file" hidden accept=".json" onChange={importAutomations} />
                        </label>
                    </MenuItem>
                </Menu>
            </>
        ) : (
            <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => setAddDialogOpen(true)}>+ Add</Button>
                <Button variant="contained" disabled={!selected} onClick={() => setCloneModalOpen(true)}>Clone</Button>
                <Button variant="outlined" onClick={exportAutomations}>Export</Button>
                <Button component="label" variant="outlined">
                    Import
                    <input type="file" accept=".json" hidden onChange={importAutomations} />
                </Button>
            </Stack>
        )
    );

    return (
        <Box>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Automation Rules</Typography>
                {renderToolbar()}
            </Box>

            {editorOpen && editAutomation ? (
                <AutomationEditor
                    automation={editAutomation}
                    onClose={() => {
                        setEditorOpen(false);
                        setEditAutomation(null);
                    }}
                    onSave={handleSave}
                />
            ) : (
<Paper elevation={2} sx={{ p: 1, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
  <Box sx={{ height: "60vh", width: "100%" }}>
    <SafeAgGrid
      columnDefs={columnDefs}
      rowData={automations}
      rowSelection="single"
      domLayout="autoHeight"
      enableQuickSearch={true}
      enableGrouping={true}
      loading={false} // or true if you are fetching automations
      groupLocked={false}
      pagination={false}
    />
  </Box>
</Paper>
            )}

            <Dialog open={cloneModalOpen} onClose={() => setCloneModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Clone Automation</DialogTitle>
                <DialogContent>
                    <Typography mb={1}>Original: <strong>{selected?.name}</strong></Typography>
                    <TextField
                        fullWidth
                        label="New Name"
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCloneModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={cloneAutomation}>Clone</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New Automation</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Automation Name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Trigger Event</InputLabel>
                        <Select
                            value={newTrigger}
                            onChange={(e) => setNewTrigger(e.target.value)}
                            label="Trigger Event"
                        >
                            <MenuItem value="INSERT">INSERT</MenuItem>
                            <MenuItem value="UPDATE">UPDATE</MenuItem>
                            <MenuItem value="DELETE">DELETE</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={addNewAutomation} disabled={!newName.trim()}>Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export { AutomationPanel };
